import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Newspaper, Upload, Loader2 } from "lucide-react";
import api from "../../lib/api";

const toParis = (iso) => {
  const p = new Intl.DateTimeFormat("fr-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date(iso));
  const g = (t) => p.find((x) => x.type === t)?.value;
  return { date: `${g("year")}-${g("month")}-${g("day")}`, time: `${g("hour")}:${g("minute")}` };
};
const isLive = (a) => a.status === "scheduled" && new Date(a.publish_at) <= new Date();

const Row = ({ a, onSave }) => {
  const init = toParis(a.publish_at);
  const [date, setDate] = useState(init.date);
  const [time, setTime] = useState(init.time);
  const changed = date !== init.date || time !== init.time;
  return (
    <tr className="border-t border-brand-soft" data-testid={`admin-article-${a.slug}`}>
      <td className="py-2 pr-3">
        <a href={`/blog/${a.slug}?apercu=1`} target="_blank" rel="noreferrer" className="font-medium text-brand-greenDeep hover:underline" data-testid={`admin-article-preview-${a.slug}`}>{a.title}</a>
        <span className="block text-[11px] text-brand-green/50 mt-0.5">
          <a href={`/blog/${a.slug}?apercu=1`} target="_blank" rel="noreferrer" className="underline">Aperçu</a>
          {a.newsletter_sent_at && <> · Newsletter envoyée le {new Date(a.newsletter_sent_at).toLocaleDateString("fr-FR")} ({a.newsletter_sent ?? 0} e-mails)</>}
        </span>
        {a.review_before_publish && <p className="text-[11px] text-amber-700 mt-0.5">À relire : {a.review_before_publish}</p>}
      </td>
      <td className="py-2 pr-3 whitespace-nowrap">
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${isLive(a) ? "bg-emerald-50 text-emerald-700" : a.status === "draft" ? "bg-brand-soft text-brand-green/70" : "bg-[#F6F2EA] text-[#8a6a3c]"}`} data-testid={`admin-article-state-${a.slug}`}>
          {isLive(a) ? "En ligne" : a.status === "draft" ? "Brouillon" : "Programmé"}
        </span>
      </td>
      <td className="py-2 pr-3 whitespace-nowrap">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border border-brand-soft rounded px-2 py-1 text-xs" data-testid={`admin-article-date-${a.slug}`} />
        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="border border-brand-soft rounded px-2 py-1 text-xs ml-1" data-testid={`admin-article-time-${a.slug}`} />
        {changed && <button onClick={() => onSave(a.slug, { publish_date: date, publish_time: time })} className="ml-1 text-xs font-semibold text-brand-greenDeep underline" data-testid={`admin-article-save-date-${a.slug}`}>Enregistrer</button>}
      </td>
      <td className="py-2">
        <select value={a.status} onChange={(e) => onSave(a.slug, { status: e.target.value })} className="border border-brand-soft rounded px-2 py-1 text-xs" data-testid={`admin-article-status-${a.slug}`}>
          <option value="scheduled">Programmé</option>
          <option value="draft">Brouillon</option>
        </select>
      </td>
    </tr>
  );
};

// Admin : articles du blog en base (statut, date de publication, import JSON).
export const AdminBlogArticles = () => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/admin/blog/articles").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const save = async (slug, patch) => {
    try { await api.put(`/admin/blog/articles/${slug}`, patch); toast.success("Article mis à jour"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const importFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const data = JSON.parse(await f.text());
      const articles = Array.isArray(data) ? data : data.articles;
      if (!Array.isArray(articles)) throw new Error("Le fichier doit contenir une liste « articles ».");
      const r = await api.post("/admin/blog/import", { articles });
      toast.success(`${r.data.created} créé(s), ${r.data.updated} mis à jour`);
      if (r.data.errors?.length) toast.error(r.data.errors.join(" · "));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message || "Fichier invalide"); }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-[10px] border border-brand-soft p-6 space-y-3" data-testid="admin-blog-articles">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-semibold text-brand-greenDeep flex items-center gap-2"><Newspaper className="w-4 h-4" /> Articles programmés</h3>
        <button onClick={() => fileRef.current?.click()} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-brand-soft text-xs font-semibold" data-testid="admin-blog-import-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Importer un fichier JSON
        </button>
        <input ref={fileRef} type="file" accept="application/json,.json" className="hidden" onChange={importFile} data-testid="admin-blog-import-input" />
      </div>
      <p className="text-xs text-brand-green/50">Heure de Paris. Un article programmé apparaît seul à sa date. Un slug déjà présent est mis à jour, jamais dupliqué. Le jour de sa publication à 8 h, les abonnés newsletter reçoivent un e-mail pour chaque nouvel article.</p>
      {list === null ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-brand-green/50"><th className="py-1 pr-3">Article</th><th className="pr-3">État</th><th className="pr-3">Publication</th><th>Statut</th></tr></thead>
            <tbody>{list.map((a) => <Row key={`${a.slug}-${a.publish_at}-${a.status}`} a={a} onSave={save} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};
